/**
 * ExportSaveDialog - Modal for exporting audio sessions
 */
import React, { memo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Download } from 'lucide-react';

const ExportSaveDialog = memo(({
  isVisible,
  filename,
  onFilenameChange,
  onClose,
  onExport,
}) => {
  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="export-save-dialog">
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-xl max-w-md w-full mx-4 border border-white/20">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Download className="w-5 h-5 text-purple-400" />
          Export Audio Session
        </h3>

        <div className="space-y-4">
          <div>
            <label className="text-white text-sm font-medium mb-2 block">
              Filename (without extension)
            </label>
            <Input
              value={filename}
              onChange={(e) => onFilenameChange(e.target.value)}
              className="bg-white/10 border-white/30 text-white"
              placeholder="Enter filename..."
              data-testid="export-filename-input"
            />
            <div className="text-white/60 text-xs mt-1">
              Preview: {filename}.webm
            </div>
          </div>

          <div className="bg-purple-500/20 border border-purple-500/30 p-3 rounded-lg">
            <div className="text-purple-300 text-sm font-medium mb-1 flex items-center gap-2">
              <span>🎵</span> High-Quality Recording
            </div>
            <div className="text-white/70 text-xs space-y-1">
              <div>48 kHz sample rate (studio quality)</div>
              <div>510 kbps bitrate (Opus fullband)</div>
              <div>Full 20 kHz frequency response</div>
              <div>Preserves all high-frequency detail</div>
            </div>
          </div>

          <div className="bg-blue-500/20 border border-blue-500/30 p-3 rounded-lg">
            <div className="text-blue-300 text-sm font-medium mb-1 flex items-center gap-2">
              <span>💾</span> Save Location Options
            </div>
            <div className="text-white/70 text-xs space-y-1">
              <div>Modern browsers: Choose exact save location</div>
              <div>Older browsers: Save to default Downloads folder</div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={onClose}
              className="flex-1 bg-gray-600 hover:bg-gray-700"
              data-testid="export-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={onExport}
              disabled={!filename.trim()}
              className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              data-testid="export-confirm-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

ExportSaveDialog.displayName = 'ExportSaveDialog';

export default ExportSaveDialog;
